import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDemoAccess } from '@/contexts/DemoAccessContext';
import { partForRoute, PART_META } from '@/lib/demoScenes';
import { useToast } from '@/components/ui/use-toast';

// ── DemoRouteGuard ────────────────────────────────────────────────────────────
// Deep links, bookmarks and the back button around the frosted nav: if a demo
// device lands on a route whose part the presentation hasn't opened yet, step
// it back to the dashboard with a friendly nudge. Renders nothing; does
// nothing unless the scene lock is ACTIVE (demo identity + live session +
// deck loaded), so real crews can never be redirected.
const DemoRouteGuard = () => {
  const { active, isRouteOpen } = useDemoAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const lastNudge = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (isRouteOpen(location.pathname)) return;
    const pk = partForRoute(location.pathname);
    const part = pk ? PART_META[pk] : null;
    navigate('/subcontractor/dashboard', { replace: true });
    // One nudge per part, not one per bounce.
    if (lastNudge.current !== pk) {
      lastNudge.current = pk;
      toast({
        title: part ? `${part.name} unlocks later in the presentation` : 'That part unlocks later',
        description: 'Your presenter opens each part of the platform as it’s shown.',
      });
    }
  }, [active, isRouteOpen, location.pathname, navigate, toast]);

  return null;
};

export default DemoRouteGuard;
